import i18n from 'i18n';
import {
  SpaceHealthState,
  SPACE_HEALTH_SEVERITY,
  SPACE_HEALTH_STATUS_I18N
} from 'enum/SpaceHealthState';
import { DeviceUnitLevel } from 'core/deviceManagement/DeviceManagement';

/**
 * 「地區」在三個地方有三個名字，指的是同一個 space attribute：
 * 畫面文案叫「地區」，`AdRequestSourceManager.getOutdoorSpaceAttributes` 回傳的
 * 選項 bag 用 `cityGroup` 當 key，送給後端的查詢欄位則叫 `region`。
 * 下拉端點 `/v2/ad-request-source/regions` 的說明寫的是「地理分區」，也是同一個。
 */
export const REGION_OPTION_BAG_KEY = 'cityGroup';

/**
 * 這一頁的篩選要用到的選項組。`getOutdoorSpaceAttributes` 一支可以取回五組，
 * 每一組是一次獨立的請求 —— 不指定就會連這一頁用不到的店號與機台特徵一起取。
 * 狀態那一組不在這裡：它的選項是前端的 enum，不是渠道底下有哪些值。
 */
export const DEVICE_FILTER_OPTION_BAG_KEYS = [
  'outdoorPlaceCategories',
  'administrativeArea',
  REGION_OPTION_BAG_KEY
];

/**
 * 篩選條件的欄位名。值沿用選項來源那一組命名，不是送給後端的參數名 ——
 * 清單端點收的是 `DEVICE_FILTER_WIRE_PARAM` 的複數名。
 */
export enum DeviceFilterField {
  PLACE_CATEGORY = 'outdoorPlaceCategories',
  ADMINISTRATIVE_AREA = 'administrativeArea',
  REGION = 'region',
  HEALTH_STATE = 'healthState'
}

/**
 * 篩選欄位 → 清單端點的查詢參數名。兩邊不同名，送出前一定要換；
 * 直接拿 enum 值當參數名送，後端整組收不到，而且不會報錯，只會當作沒篩。
 */
export const DEVICE_FILTER_WIRE_PARAM: Record<DeviceFilterField, string> = {
  [DeviceFilterField.PLACE_CATEGORY]: 'placeCategories',
  [DeviceFilterField.ADMINISTRATIVE_AREA]: 'districts',
  [DeviceFilterField.REGION]: 'regions',
  [DeviceFilterField.HEALTH_STATE]: 'healthStates'
};

/** 把畫面上的篩選條件換成清單端點收得到的參數，空的條件不送。 */
export function toDeviceFilterParams(
  filter: DeviceManagementFilter
): Record<string, string[]> {
  return Object.entries(filter).reduce<Record<string, string[]>>(
    (params, [field, values]) => {
      if (values.length > 0) {
        params[DEVICE_FILTER_WIRE_PARAM[field as DeviceFilterField]] = values;
      }
      return params;
    },
    {}
  );
}

/**
 * 每個條件的行為提示，顯示在該條件的下拉裡。
 * 區域特性與行政區是父/子兩層，另外兩個是單層，提示要說清楚差別。
 */
export const DEVICE_FILTER_HINT_I18N: Record<DeviceFilterField, string> = {
  [DeviceFilterField.PLACE_CATEGORY]: 'deviceManagement.hints.placeCategory',
  [DeviceFilterField.ADMINISTRATIVE_AREA]:
    'deviceManagement.hints.administrativeArea',
  [DeviceFilterField.REGION]: 'deviceManagement.hints.region',
  [DeviceFilterField.HEALTH_STATE]: 'deviceManagement.hints.healthState'
};

/**
 * 選項到底取回來了沒。
 *
 * 三種狀態在畫面上都是一片空白，但意思不一樣：還在取的要等，取到空的是這個渠道
 * 真的沒有那種值，取失敗的則是這一頁幫不上忙、請他重整。少了最後那一種，任何一支
 * 選項端點掛掉，下拉就永遠停在「載入中」—— 取選項那支 effect 只在換渠道時才會
 * 再跑一次，使用者除了重整沒有別的路，而畫面上沒有任何線索。
 */
export enum DeviceFilterOptionsState {
  LOADING = 'loading',
  LOADED = 'loaded',
  FAILED = 'failed'
}

/** 每一種狀態下，空的選項清單要寫的那一句話。 */
export const DEVICE_FILTER_OPTIONS_EMPTY_I18N: Record<
  DeviceFilterOptionsState,
  string
> = {
  [DeviceFilterOptionsState.LOADING]:
    'deviceManagement.messages.loadingFilterOptions',
  [DeviceFilterOptionsState.LOADED]: 'deviceManagement.messages.noFilterOption',
  [DeviceFilterOptionsState.FAILED]:
    'deviceManagement.messages.filterOptionsLoadFailed'
};

/**
 * 已選的值換回選項物件。
 *
 * 版位選擇器那一套（`components/CampaignLimitation/SelectComponent`）進出都是
 * `SelectOptions`，而這一頁的條件存的是要送給後端的字串，所以進去之前對回來一次。
 * 父層與子項都要找：勾父層時存的是父層代碼，勾子項時存的是 `父/子`。
 *
 * 找不到對應選項的值仍然保留（組成一個只有 value 的選項），否則換渠道時
 * 選項還沒回來，已選的值會在這一步被默默吃掉。
 */
export function findFilterOptions(
  options: SelectOptions[],
  values: string[]
): SelectOptions[] {
  const byValue = new Map<string, SelectOptions>();
  const collect = (option: SelectOptions) => {
    byValue.set(String(option.value), option);
    (option.options ?? []).forEach(collect);
  };
  options.forEach(collect);
  return values.map((value) => byValue.get(value) ?? { label: value, value });
}

/** 四個可複選的篩選條件，值一律是送給後端的原始字串。 */
export type DeviceManagementFilter = {
  [DeviceFilterField.PLACE_CATEGORY]: string[];
  [DeviceFilterField.ADMINISTRATIVE_AREA]: string[];
  [DeviceFilterField.REGION]: string[];
  [DeviceFilterField.HEALTH_STATE]: string[];
};

export const EMPTY_FILTER: DeviceManagementFilter = {
  [DeviceFilterField.PLACE_CATEGORY]: [],
  [DeviceFilterField.ADMINISTRATIVE_AREA]: [],
  [DeviceFilterField.REGION]: [],
  [DeviceFilterField.HEALTH_STATE]: []
};

/**
 * 進頁與換渠道時的篩選條件：狀態先勾好「停止服務」以外的全部。
 *
 * 停止服務的版位多半是已經下架的機台，其中還有 RMax 那邊刪掉的測試機台 ——
 * PMax 只收得到它們被停用，收不到被刪除，所以會一直留在清單裡，而且照嚴重度
 * 排序時排在最前面。預設排除之後，使用者先看到的是還在營運的機台。
 *
 * 用「勾選其餘狀態」表達排除：後端比對的是收斂後的單一狀態，而收斂結果只會是
 * 這四種之一（認不得的值一律算正常），所以勾其餘三種與「不是停止服務」等價。
 *
 * 「清除篩選」回到 `EMPTY_FILTER` 而不是這一份：清除之後就是全部，
 * 那也是使用者要看停止服務機台時最直接的路。
 */
export const DEFAULT_FILTER: DeviceManagementFilter = {
  ...EMPTY_FILTER,
  [DeviceFilterField.HEALTH_STATE]: Object.values(SpaceHealthState)
    .filter((state) => state !== SpaceHealthState.BANNED)
    .sort((a, b) => SPACE_HEALTH_SEVERITY[a] - SPACE_HEALTH_SEVERITY[b])
    .map((state) => state.toUpperCase())
};

/**
 * 兩個分頁共用的查詢條件。條件由上層持有，換分頁不會重設，
 * 兩個分頁拿到的一定是同一份。
 */
export type DeviceQueryConditions = {
  salesChannel?: number;
  /**
   * 目前渠道的投遞單位。清單的欄位組合以回應每一列的 `unitLevel` 為準，
   * 這個值只在清單還沒有任何一列時當作備援，讓表頭不會少一欄。
   */
  channelDeliveryUnit?: DeviceUnitLevel;
  filter: DeviceManagementFilter;
  /** 匯入名單限定的店號；空陣列代表沒有名單條件。 */
  storeNums: string[];
  /**
   * 這個分頁現在看得到嗎。看不到的分頁不發查詢，回到它的時候條件沒變也不重查
   * —— 兩個分頁的查詢狀態都掛在頁面這一層，換分頁不會把它們卸載掉。
   */
  active: boolean;
};

/**
 * 狀態下拉的選項。文案與排序都沿用廣告活動版位選擇器那一組
 * （`SPACE_HEALTH_STATUS_I18N` / `SPACE_HEALTH_SEVERITY`），刻意不另立一套 ——
 * 同一個狀態在兩個畫面上叫不同名字，使用者沒有辦法對應起來。
 *
 * option 的 value 是後端的大寫格式，enum 值則是小寫；兩者的換算一律走
 * `parseSpaceHealthState`，不要在呼叫端自己比對字串。
 */
export function buildHealthStateOptions(): SelectOptions[] {
  return Object.values(SpaceHealthState)
    .sort((a, b) => SPACE_HEALTH_SEVERITY[a] - SPACE_HEALTH_SEVERITY[b])
    .map((state) => ({
      label: i18n.t(SPACE_HEALTH_STATUS_I18N[state]),
      value: state.toUpperCase()
    }));
}
